"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseTextElement = parseTextElement;
exports.parseLabel = parseLabel;
exports.parseTextBox = parseTextBox;
exports.extractLabelInfo = extractLabelInfo;
const Util_1 = require("../../Util");
const getMirrorValuesToKeepTextRightSideUp_1 = require("../schematic/getMirrorValuesToKeepTextRightSideUp");
const parseTextTransforms_1 = require("../schematic/parseTextTransforms");
const v6Common_1 = require("./v6Common");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Utils_1 = require("@cadstrom/sigma-engine-shared/dist/Utils");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const Constants_1 = require("../../Constants");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
function parseTextElement(node, isModule, warnings, defaultLayerName, layerSelector, keepRightSideUp) {
    let [value] = node.values;
    value = (0, Util_1.removeQuotes)(value);
    const { pos, direction, justify, fontSize, thickness, attr, mirror, layerName, textAlign } = extractLabelInfo(node, isModule, warnings);
    let layer;
    if (layerName) {
        const layers = layerSelector.filterLayers(layerName);
        if (layers[0]) {
            layer = layers[0];
        }
    }
    if (!layer) {
        if (defaultLayerName) {
            const layers = layerSelector.filterLayers(defaultLayerName);
            if (layers[0]) {
                layer = layers[0];
            }
        }
        else {
            layer = layerSelector.getLayerByType(sigma_engine_shared_2.DesignModels.UnknownLayerType.Other);
        }
    }
    return {
        drawing: (0, parseTextTransforms_1.parseTextTransforms)({
            type: 'text',
            value: value,
            pos: pos,
            angle: direction,
            justify,
            textAlign,
            size: fontSize,
            thickness: typeof thickness === 'number' ? thickness : 0,
            attr,
            mirror: keepRightSideUp ? (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction) : mirror,
        }),
        layers: [layer.id],
    };
}
function parseLabel(model, node, isModule, warnings, netRefCtx, defaultLayerName, layerSelector, resolver, keepRightSideUp) {
    let [value] = node.values;
    value = (0, Util_1.removeQuotes)(value);
    const { pos, direction, justify, fontSize, thickness, attr, mirror, layerName, textAlign } = extractLabelInfo(node, isModule, warnings);
    let layer;
    if (layerName) {
        const layers = layerSelector.filterLayers(layerName);
        if (layers[0]) {
            layer = layers[0];
        }
    }
    if (!layer) {
        if (defaultLayerName) {
            const layers = layerSelector.filterLayers(defaultLayerName);
            if (layers[0]) {
                layer = layers[0];
            }
        }
        else {
            layer = layerSelector.getLayerByType(sigma_engine_shared_2.DesignModels.UnknownLayerType.Other);
        }
    }
    return {
        drawing: (0, parseTextTransforms_1.parseTextTransforms)({
            type: 'text',
            value: sigma_engine_shared_1.NetRef.fromString(netRefCtx, resolver ? resolver.validateSourceNetName(model.designId, value, 'label') : value).strCast(),
            pos: pos,
            angle: direction,
            justify,
            textAlign,
            size: fontSize,
            thickness: typeof thickness === 'number' ? thickness : 0,
            attr,
            mirror: keepRightSideUp ? (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction) : mirror,
        }),
        layers: [layer.id],
    };
}
function parseTextBox(node, isModule, warnings, defaultLayerName, layerSelector, keepRightSideUp) {
    let [value] = node.values;
    value = (0, Util_1.removeQuotes)(value);
    let { pos, direction, justify, fontSize, thickness, attr, mirror, layerName, textAlign, size, stroke, fill } = extractLabelInfo(node, isModule, warnings, layerSelector);
    if (!size) {
        warnings.push(`${node.name} missing expected size attribute`);
        size = [0, 0];
    }
    let layer;
    if (layerName) {
        const layers = layerSelector.filterLayers(layerName);
        if (layers[0]) {
            layer = layers[0];
        }
    }
    if (!layer) {
        if (defaultLayerName) {
            const layers = layerSelector.filterLayers(defaultLayerName);
            if (layers[0]) {
                layer = layers[0];
            }
        }
        else {
            layer = layerSelector.getLayerByType(sigma_engine_shared_2.DesignModels.UnknownLayerType.Other);
        }
    }
    const textDrawing = (0, parseTextTransforms_1.parseTextTransforms)({
        type: 'text',
        value,
        pos: [0, 0],
        angle: direction,
        justify,
        textAlign,
        size: fontSize,
        padding: [fontSize, fontSize],
        bounds: size.map((v) => Math.abs(v)),
        thickness: typeof thickness === 'number' ? thickness : 0,
        attr,
        mirror: keepRightSideUp ? (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction) : mirror,
    });
    const rectDrawing = {
        type: 'rect',
        start: [0, 0],
        end: size.map((v) => Math.abs(v)),
        filled: fill?.filled === 1,
        strokeWidth: (0, Utils_1.max)(stroke?.width || 0, Constants_1.SEGMENT_MIN_WIDTH),
        colorOverride: fill?.colorOverride,
    };
    const groupDrawing = {
        type: 'group',
        pos: (0, Transform_1.translatePoint)(pos, size.map((v) => Math.min(0, v))),
        angle: direction,
        drawings: [rectDrawing, textDrawing],
    };
    return {
        drawing: groupDrawing,
        layers: [layer.id],
    };
}
function extractLabelInfo(node, isModule, warnings, layerSelector, defaultFillLayer = sigma_engine_shared_2.DesignModels.UnknownLayerType.Other) {
    let pos = [0, 0];
    let size;
    let fontSize = 0;
    let direction = 0;
    const justify = [0, 0];
    let justifyNodeValues;
    let shape;
    let thickness;
    let attr;
    const mirror = [false, false];
    let layerName;
    let stroke;
    let fill;
    const flipY = isModule ? -1 : 1;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                pos = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
                if (n.values.length > 2) {
                    direction = parseFloat(n.values[2]);
                }
                break;
            }
            case 'size': {
                size = [n.values[0], n.values[1]].map((v) => parseFloat(v));
                break;
            }
            case 'effects': {
                const fontNode = n.nodes.find((n) => n.name === 'font');
                if (fontNode) {
                    const sizeNode = fontNode.nodes.find((n) => n.name === 'size');
                    if (sizeNode) {
                        fontSize = parseFloat(sizeNode.values[0]);
                    }
                    const thicknessNode = fontNode.nodes.find((n) => n.name === 'thickness');
                    if (thicknessNode) {
                        thickness = parseFloat(thicknessNode.values[0]);
                    }
                    if (fontNode.values.indexOf('bold') !== -1) {
                        if (!attr) {
                            attr = [];
                        }
                        attr.push('bold');
                    }
                    if (fontNode.values.indexOf('italic') !== -1) {
                        if (!attr) {
                            attr = [];
                        }
                        attr.push('italic');
                    }
                }
                justifyNodeValues = justifyNodeValues || n.nodes.find((n) => n.name === 'justify')?.values;
                break;
            }
            case 'shape': {
                switch (n.values[0]) {
                    case 'input':
                    case 'tri_state':
                    case 'output':
                    case 'bidirectional':
                    case 'passive':
                        shape = n.values[0];
                        break;
                    default:
                        warnings.push(`Unknown ${node.name} shape: ${n.values[0]}`);
                }
                break;
            }
            case 'layer':
                layerName = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            case 'stroke':
                stroke = (0, v6Common_1.parseStrokeDirective)(n);
                break;
            case 'fill':
                if (layerSelector) {
                    fill = (0, v6Common_1.parseFillDirective)(n, layerSelector, defaultFillLayer);
                }
                break;
        }
    });
    if (justifyNodeValues) {
        for (const val of justifyNodeValues) {
            switch (val) {
                case 'right':
                    justify[0] = 1;
                    break;
                case 'left':
                    justify[0] = -1;
                    break;
                case 'bottom':
                    justify[1] = 1;
                    break;
                case 'top':
                    justify[1] = -1;
                    break;
                case 'mirror':
                    mirror[1] = true;
                    break;
            }
        }
    }
    let textAlign = 'left';
    switch (justify[0]) {
        case 0:
            textAlign = 'center';
            break;
        case 1:
            textAlign = 'right';
            break;
    }
    return {
        pos,
        direction,
        justify,
        fontSize,
        shape,
        thickness,
        attr,
        mirror,
        layerName,
        textAlign,
        size,
        stroke,
        fill,
    };
}
//# sourceMappingURL=v6ParseLabel.js.map